// 线性马达 / 振动反馈 —— 用于移动端提升消除成功、失败等操作的手感。
//
// Android / 支持 Vibration API 的浏览器：直接用 navigator.vibrate。
// iOS（含 iOS 上的 Chrome —— 它们都强制使用 WebKit 内核）：Safari 从未实现
//   Vibration API，navigator.vibrate 不存在，所以旧实现在 iOS 上完全无效。
//   退路：Safari 17.4+ 引入的 <input type="checkbox" switch> 在切换时会触发系统
//   Taptic 触感；对其关联的 <label> 触发 click() 即可间接调用马达。
//   注意：此 hack 已在 iOS 26.5 被 Apple 修复（无法再通过脚本 click 触发），
//   但对 iOS 18 ~ 26.4 仍然有效 —— 属尽力而为的降级方案，失效时静默降级为无反馈。

const canVibrate =
  typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

// iPadOS 13+ 在桌面 UA 下会伪装成 Mac，用触点数辅助判定真机。
const isIOS =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.platform) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
    /iPhone|iPad|iPod/.test(navigator.userAgent));

let switchLabel: HTMLLabelElement | null = null;

// 懒创建一个隐藏的 <label><input type="checkbox" switch></label>。
// 关键：保持在渲染树内（用 opacity/pointer-events 隐藏，而非 display:none，
// 否则 WebKit 不会触发触感），且必须 click 的是 label 而非 input 本身。
function ensureSwitch(): HTMLLabelElement | null {
  if (typeof document === "undefined" || !document.body) return null;
  if (switchLabel) return switchLabel;
  const label = document.createElement("label");
  label.setAttribute("aria-hidden", "true");
  Object.assign(label.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "1px",
    height: "1px",
    opacity: "0",
    pointerEvents: "none",
    zIndex: "-1",
  } as Partial<CSSStyleDeclaration>);
  const input = document.createElement("input");
  input.type = "checkbox";
  input.setAttribute("switch", "");
  input.tabIndex = -1;
  label.appendChild(input);
  document.body.appendChild(label);
  switchLabel = label;
  return label;
}

// 连续触发 times 记触感，用 gap(ms) 拉出节奏。首记同步触发以尽量落在用户手势内。
function iosTap(times: number, gap: number): void {
  const label = ensureSwitch();
  if (!label) return;
  const fire = (): void => {
    try {
      label.click();
    } catch {
      // 忽略：某些版本/上下文下会抛错，静默降级。
    }
  };
  fire();
  for (let i = 1; i < times; i++) setTimeout(fire, i * gap);
}

// 统一入口：iOS 走 switch hack，其余平台走 Vibration API。
function haptic(pattern: number | number[], iosTaps: number): void {
  if (isIOS) {
    iosTap(iosTaps, 120);
    return;
  }
  if (!canVibrate) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // 某些浏览器在无用户手势时会抛错，忽略即可。
  }
}

/** 消除成功：一记干脆利落的短震。 */
export function hapticSuccess(): void {
  haptic(20, 1);
}

/** 操作失败 / 无效滑动：两记轻微的“咯噔”震动，提示不可行。 */
export function hapticFailure(): void {
  haptic([15, 40, 15], 2);
}

/** 通关：稍强的庆祝式节奏震动。 */
export function hapticWin(): void {
  haptic([30, 50, 30, 50, 60], 3);
}
